/**
 * GetStartedModal - Generic onboarding modal for first-time users
 *
 * Presents configurable options for populating data in a section.
 * Supports custom title, subtitle, and method configurations.
 *
 * @template T - Type of method ID (defaults to string for flexibility)
 *
 * @example
 * // Asset section usage
 * <GetStartedModal
 *   open={isOpen}
 *   onClose={handleClose}
 *   onSelectMethod={handleMethodSelect}
 *   methods={ASSET_GET_STARTED_METHODS}
 * />
 *
 * @example
 * // Vulnerability section with custom title
 * <GetStartedModal
 *   open={isOpen}
 *   onClose={handleClose}
 *   onSelectMethod={handleMethodSelect}
 *   title="Get Started with Vulnerabilities"
 *   subtitle="Choose how you'd like to discover vulnerabilities:"
 *   methods={VULNERABILITY_GET_STARTED_METHODS}
 * />
 */

import { Modal } from '@/components/Modal';

import { MethodCard } from './MethodCard';
import type { GetStartedModalProps } from './types';

export function GetStartedModal<T extends string = string>({
  open,
  onClose,
  onSelectMethod,
  title = 'Get Started with Chariot',
  subtitle = "Choose how you'd like to populate your attack surface:",
  methods,
}: GetStartedModalProps<T>) {
  return (
    <Modal open={open} onClose={onClose} title={title} size="3xl" hideFooter>
      <div className="flex flex-col gap-6 py-8 pb-16">
        <p className="text-chariot-text-secondary text-sm">{subtitle}</p>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          {methods.map(method => (
            <MethodCard
              key={method.id}
              method={method}
              onClick={() => onSelectMethod(method.id)}
            />
          ))}
        </div>
      </div>
    </Modal>
  );
}

export type { GetStartedMethod, GetStartedModalProps } from './types';
